import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Paper, Button, Card, CardContent, Avatar,
  List, ListItemButton, ListItemText, ListItemAvatar, CircularProgress,
  Chip, Stack, Divider,
} from '@mui/material';
import {
  FolderSpecial as PathsIcon,
  MenuBook as StoriesIcon,
  People as UsersIcon,
  Add as AddIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../services/apiClient';

/**
 * AdminDashboard — modern landing. Loads only paths + users (no N+1): the total
 * story count is summed from each path's `stories_count`.
 */
const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ paths: 0, stories: 0, users: 0 });
  const [recentPaths, setRecentPaths] = useState([]);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [pathsRes, usersRes] = await Promise.all([
          apiClient.admin.getPaths(),
          apiClient.admin.getUsers().catch(() => ({ data: { users: [] } })),
        ]);
        if (!active) return;
        const paths = pathsRes.data.paths || [];
        const storyTotal = paths.reduce((sum, p) => sum + (p.stories_count || 0), 0);
        setCounts({ paths: paths.length, stories: storyTotal, users: usersRes.data.users?.length || 0 });
        setRecentPaths(
          [...paths].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)).slice(0, 6)
        );
      } catch (e) {
        console.error('dashboard load failed', e);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const stat = (label, value, icon, color, to) => (
    <Paper
      variant="outlined"
      onClick={() => navigate(to)}
      sx={{
        p: 2.5, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer',
        transition: 'box-shadow .15s, border-color .15s, transform .15s',
        '&:hover': { boxShadow: 3, borderColor: `${color}.main`, transform: 'translateY(-2px)' },
      }}
    >
      <Avatar variant="rounded" sx={{ bgcolor: `${color}.main`, width: 52, height: 52 }}>{icon}</Avatar>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1 }}>
          {loading ? <CircularProgress size={22} /> : value}
        </Typography>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
      </Box>
    </Paper>
  );

  const fmt = (d) => (d ? new Date(d).toLocaleDateString() : '—');

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" sx={{ mb: 3, gap: 1 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            Welcome back{user?.first_name ? `, ${user.first_name}` : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary">Manage your learning paths, stories, and content.</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => navigate('/admin/stories/new')} sx={{ textTransform: 'none', borderRadius: 2 }}>
            New Story
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/admin/paths/new')} sx={{ textTransform: 'none', borderRadius: 2 }}>
            New Path
          </Button>
        </Stack>
      </Stack>

      {/* Stats */}
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>{stat('Paths', counts.paths, <PathsIcon />, 'primary', '/admin/paths')}</Grid>
        <Grid item xs={12} sm={4}>{stat('Stories', counts.stories, <StoriesIcon />, 'secondary', '/admin/stories')}</Grid>
        <Grid item xs={12} sm={4}>{stat('Users', counts.users, <UsersIcon />, 'info', '/admin/users')}</Grid>
      </Grid>

      {/* Recent paths */}
      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent sx={{ pb: 0 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Recent paths</Typography>
            <Button size="small" onClick={() => navigate('/admin/paths')} sx={{ textTransform: 'none' }}>View all</Button>
          </Stack>
        </CardContent>
        <Divider />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : recentPaths.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>No paths yet.</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/admin/paths/new')} sx={{ textTransform: 'none' }}>
              Create your first path
            </Button>
          </Box>
        ) : (
          <List disablePadding>
            {recentPaths.map((p, i) => (
              <React.Fragment key={p.path_id}>
                <ListItemButton onClick={() => navigate(`/admin/paths/${p.path_id}`)} sx={{ py: 1.5 }}>
                  <ListItemAvatar>
                    <Avatar variant="rounded" sx={{ bgcolor: 'primary.main' }}><PathsIcon /></Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={<Typography sx={{ fontWeight: 600 }}>{p.title}</Typography>}
                    secondary={`${p.level || '—'} · ${p.stories_count || 0} stories · updated ${fmt(p.updated_at)}`}
                  />
                  <Stack direction="row" spacing={1} alignItems="center">
                    {p.is_published
                      ? <Chip label="Published" color="success" size="small" variant="outlined" />
                      : <Chip label="Draft" size="small" variant="outlined" />}
                    <ChevronRightIcon color="action" />
                  </Stack>
                </ListItemButton>
                {i < recentPaths.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Card>
    </Box>
  );
};

export default AdminDashboard;
